import { useState, useEffect, lazy, Suspense } from "react";
import { motion } from "framer-motion"

// Imports Assets
import logo from "./assets/logo.webp";
import hero from "./assets/header_img.webp";
import about from "./assets/brand_img.webp";
import project1 from "./assets/project_img_1.webp";
import project2 from "./assets/project_img_2.webp";
import project3 from "./assets/project_img_3.webp";
import project4 from "./assets/project_img_4.webp";
import project5 from "./assets/project_img_5.webp";
import project6 from "./assets/project_img_6.webp";
import testimonial1 from "./assets/profile_img_1.webp";
import testimonial2 from "./assets/profile_img_2.webp";
import testimonial3 from "./assets/profile_img_3.webp";
import logoFooter from "./assets/logo_dark.webp";

// Imports Components
const Title = lazy(() => import("./components/Title"))
const ProjectsCard = lazy(() => import("./components/ProjectsCard"))
const TestimonialsCard = lazy(() => import("./components/TestimonialsCard"))

const Loader = () => <div className="w-10 h-10 rounded-full border-4 border-gray-200 border-t-blue-600 animate-spin"></div>;

function App() {
  const [showNav, setShowNav] = useState(false);
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [formData, setFormData] = useState({ name: "", email: "", message: "" });

  const Projects = [
    { image: project1, title: "Skyline Haven", price: "$250,000", location: "California" },
    { image: project2, title: "Vista Verde", price: "$200,000", location: "San Francisco" },
    { image: project3, title: "Serenity Suites", price: "$300,000", location: "Chicago" },
    { image: project4, title: "Central Square", price: "$400,000", location: "Los Angeles" },
    { image: project5, title: "Vista Verde 2", price: "$500,000", location: "San Francisco" },
    { image: project6, title: "Modern Loft", price: "$600,000", location: "Philadelphia" }
  ];

  const Testimonials = [
    { image: testimonial1, name: "Donald Jackman", job: "Marketing Manager", star: 5, description: "From the very first meeting, they understood my vision and helped me find the perfect property." },
    { image: testimonial2, name: "Richard Nelson", job: "UI/UX Designer", star: 4, description: "Their attention to detail and commitment to client satisfaction is truly unmatched." },
    { image: testimonial3, name: "James Washington", job: "Co-Founder", star: 5, description: "Professional, dedicated, and highly visionary team. Highly recommended!" },
  ];

  // منع التمرير عند فتح القائمة في الموبايل
  useEffect(() => {
    document.body.style.overflow = showNav ? "hidden" : "auto";
  }, [showNav]);

  const handleSubmit = (e) => {
    e.preventDefault();
    setShowSnackbar(true);
    setTimeout(() => setShowSnackbar(false), 4000);
    setFormData({ name: "", email: "", message: "" });
  };

  return (
    <div className="overflow-x-hidden font-sans antialiased">

      {/* 1. Hero & Navbar Section */}
      <section
        id="home"
        style={{ backgroundImage: `url(${hero})` }}
        className="min-h-screen bg-cover bg-center flex flex-col relative"
      >
        <nav className="flex items-center justify-between px-6 md:px-16 py-6 w-full absolute top-0 z-50">
          <img src={logo} alt="Estate Logo" loading="lazy" className="w-28 md:w-32" />
          {/* Desktop Links */}
          <ul className="hidden md:flex gap-8 text-white font-medium">
            <li><a href="#home" className="hover:text-blue-400 transition">Home</a></li>
            <li><a href="#about" className="hover:text-blue-400 transition">About</a></li>
            <li><a href="#projects" className="hover:text-blue-400 transition">Projects</a></li>
            <li><a href="#testimonials" className="hover:text-blue-400 transition">Testimonials</a></li>
          </ul>

          <button className="hidden md:block bg-white text-black px-7 py-2 rounded-full font-bold hover:bg-gray-200 transition">
            Sign up
          </button>

          {/* Mobile Menu Icon */}
          <button onClick={() => setShowNav(true)} aria-label="Open Menu" className="md:hidden flex flex-col gap-1.5 p-2">
            <span className="w-8 h-0.5 bg-white"></span>
            <span className="w-8 h-0.5 bg-white"></span>
            <span className="w-8 h-0.5 bg-white"></span>
          </button>
        </nav>

        {/* Mobile Navigation Overlay */}
        <div className={`fixed inset-0 bg-white z-[60] flex flex-col items-center justify-center transition-transform duration-500 ${showNav ? 'translate-x-0' : 'translate-x-full'}`}>
          <button onClick={() => setShowNav(false)} aria-label="Close Menu" className="absolute top-8 right-8 text-5xl text-gray-800">&times;</button>
          <ul className="flex flex-col gap-8 text-2xl font-bold text-gray-900">
            <li><a onClick={() => setShowNav(false)} href="#home">Home</a></li>
            <li><a onClick={() => setShowNav(false)} href="#about">About</a></li>
            <li><a onClick={() => setShowNav(false)} href="#projects">Projects</a></li>
            <li><a onClick={() => setShowNav(false)} href="#testimonials">Testimonials</a></li>
          </ul>
        </div>

        {/* Hero Content */}
        <motion.div
          initial={{ opacity: 0, y: 100 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{
            duration: 1.2,
            ease: "easeOut",
            delay: 0.2
          }}
          className="flex-grow flex flex-col justify-center items-center text-center text-white px-6">
          <h1 className="text-4xl md:text-7xl font-bold leading-tight max-w-4xl animate-fade-in">
            Explore homes that <br className="hidden md:block" /> fit your dreams
          </h1>
          <div className="mt-10 flex flex-col sm:flex-row gap-4 md:gap-6">
            <a href="#projects" className="border border-white px-10 py-3 rounded-md hover:bg-white hover:text-black transition text-center">Projects</a>
            <a href="#contact" className="bg-blue-600 px-10 py-3 rounded-md hover:bg-blue-700 transition text-center">Contact Us</a>
          </div>
        </motion.div>
      </section>

      <Suspense fallback={<Loader />}>

        {/* 2. About Section */}
        <section id="about" className="container mx-auto py-20 px-6 md:px-20">
          <Title title="About" subtitle="Our Brand" description="Passionate About Properties, Dedicated to Your Vision" />
          <motion.div
            initial={{ opacity: 0, x: 100 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 1, delay: 0.7 }}
            viewport={{ once: true }}
            className="flex flex-col md:flex-row items-center justify-center gap-12 md:gap-24 mt-12">
            <img src={about} alt="Modern building architecture" loading="lazy" className="w-full max-w-md" />
            <div className="flex flex-col items-center md:items-start text-center md:text-left">
              <div className="grid grid-cols-2 gap-8 w-full">
                <div className="flex flex-col"><span className="text-4xl font-bold text-gray-800">10+</span><span className="text-gray-500">Years of Excellence</span></div>
                <div className="flex flex-col"><span className="text-4xl font-bold text-gray-800">12+</span><span className="text-gray-500">Projects Completed</span></div>
                <div className="flex flex-col"><span className="text-4xl font-bold text-gray-800">20+</span><span className="text-gray-500">Mn. Sq. Ft. Delivered</span></div>
                <div className="flex flex-col"><span className="text-4xl font-bold text-gray-800">25+</span><span className="text-gray-500">Ongoing Projects</span></div>
              </div>
              <p className="max-w-lg text-gray-600 mt-10 leading-relaxed">
                Lorem ipsum dolor sit amet consectetur adipisicing elit. Placeat quae accusamus odio, explicabo consectetur ipsam veniam? Sunt illo sed soluta!
              </p>
              <button className="mt-8 bg-blue-600 text-white px-10 py-3 rounded hover:bg-blue-700 transition shadow-lg">Learn More</button>
            </div>
          </motion.div>
        </section>

        {/* 3. Projects Section */}
        <section id="projects" className="container mx-auto py-20 px-6 md:px-20 bg-gray-50">
          <Title title="Projects" subtitle="Completed" description="Crafting Spaces, Building Legacies - Explore Our Portfolio" />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-y-16 gap-x-8 mt-16 place-items-center">
            {Projects.map((project, index) => (
              <ProjectsCard key={index} {...project} index={index} />
            ))}
          </div>
        </section>

        {/* 4. Testimonials Section */}
        <section id="testimonials" className="container mx-auto py-20 px-6 md:px-20">
          <Title title="Customer" subtitle="Testimonials" description="Real Stories from Those Who Found Home with Us" />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10 mt-16 place-items-center">
            {Testimonials.map((testimonial, index) => (
              <TestimonialsCard key={index} {...testimonial} index={index} />
            ))}
          </div>
        </section>
      </Suspense>

      {/* 5. Contact Section */}
      <section id="contact" className="container mx-auto py-20 px-6 md:px-20 bg-gray-50">
        <Title title="Contact" subtitle="With Us" description="Ready to Make a Move? Let's Build Your Future Together" />
        <motion.form
          initial={{ opacity: 0, y: -50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.5 }}
          viewport={{ once: true }}
          onSubmit={handleSubmit} className="max-w-2xl mx-auto mt-12 bg-white p-8 rounded-xl shadow-sm border border-gray-100">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="flex flex-col">
              <label className="mb-2 font-medium">Your Name</label>
              <input
                required
                className="border p-3 rounded-lg focus:border-blue-500 outline-none transition"
                type="text"
                placeholder="John Doe"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </div>
            <div className="flex flex-col">
              <label className="mb-2 font-medium">Your Email</label>
              <input
                required
                className="border p-3 rounded-lg focus:border-blue-500 outline-none transition"
                type="email"
                placeholder="example@gmail.com"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              />
            </div>
          </div>
          <div className="flex flex-col mt-6">
            <label className="mb-2 font-medium">Message</label>
            <textarea
              required
              className="border p-3 rounded-lg h-40 focus:border-blue-500 outline-none transition resize-none"
              placeholder="How can we help you?"
              value={formData.message}
              onChange={(e) => setFormData({ ...formData, message: e.target.value })}
            ></textarea>
          </div>
          <div className="text-center">
            <button className="bg-blue-600 text-white px-12 py-3 mt-8 rounded-lg hover:bg-blue-700 transition font-bold text-lg shadow-md">Send Message</button>
          </div>
        </motion.form>
      </section>

      {/* 6. Footer Section */}
      <footer className="bg-slate-900 text-white py-16 px-6 md:px-20">
        <div className="container mx-auto grid grid-cols-1 md:grid-cols-3 gap-12 border-b border-gray-700 pb-12">
          <div className="flex flex-col gap-6">
            <img src={logoFooter} alt="Estate Logo" loading="lazy" className="w-32" />
            <p className="text-gray-400 text-sm leading-relaxed">Lorem Ipsum is simply dummy text of the printing and typesetting industry. Lorem Ipsum has been the industry's standard.</p>
          </div>
          <div>
            <h3 className="text-xl font-bold mb-6">Company</h3>
            <ul className="flex flex-col gap-3 text-gray-400">
              <li><a href="#home" className="hover:text-white transition">Home</a></li>
              <li><a href="#about" className="hover:text-white transition">About us</a></li>
              <li><a href="#contact" className="hover:text-white transition">Contact us</a></li>
              <li><a href="#privacy" className="hover:text-white transition">Privacy policy</a></li>
            </ul>
          </div>
          <div>
            <h3 className="text-xl font-bold mb-6">Subscribe</h3>
            <p className="text-gray-400 mb-6 text-sm">The latest news, articles, and resources, sent to your inbox weekly.</p>
            <div className="flex gap-2">
              <input type="email" placeholder="Enter your email" className="bg-gray-800 text-white p-3 rounded w-full outline-none focus:ring-1 ring-blue-500" />
              <button className="bg-blue-600 px-6 py-3 rounded hover:bg-blue-700 font-bold transition">Subscribe</button>
            </div>
          </div>
        </div>
        <p className="text-center mt-10 text-gray-500 text-sm">Copyright {new Date().getFullYear()} &copy; All rights reserved.</p>
      </footer>

      {/* Success Snackbar */}
      {showSnackbar && (
        <div className="fixed top-10 right-10 bg-green-600 text-white px-8 py-4 rounded-xl shadow-2xl z-[100] flex items-center gap-3">
          <span className="bg-white text-green-600 rounded-full w-6 h-6 flex items-center justify-center font-bold">✓</span>
          Sent Successfully!
        </div>
      )}
    </div>
  );
}

export default App;
